import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

type SeoConfig = {
  title: string;
  description: string;
  robots?: string;
};

const SITE_NAME = 'Nightmare Syndicate Records';
const SITE_URL = 'https://nightmaresyndicaterecords.com';
const SHARE_IMAGE_URL = `${SITE_URL}/img/og-card.png`;
const DEFAULT_DESCRIPTION =
  'Independent sonic operation under the Neurotic Atomic Institute of Foreign Affairs. Hostile frequencies, compromised transmissions, records of questionable origin. Survival not guaranteed.';
const BAND_NAMES_BY_ID: Record<string, string> = {
  'fatal-exposure': 'FATAL EXPOSURE',
  'nagasaki-sunrise': 'NAGASAKI SUNRISE',
  'nightmare-syndicate': 'FALLOUT ENGINEERING 2810',
};

function upsertMetaByName(name: string, content: string) {
  let el = document.head.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertMetaByProperty(property: string, content: string) {
  let el = document.head.querySelector(`meta[property="${property}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('property', property);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertCanonical(href: string) {
  let el = document.head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function getSeoConfig(pathname: string): SeoConfig {
  if (pathname === '/') {
    return {
      title: SITE_NAME,
      description: DEFAULT_DESCRIPTION,
      robots: 'index,follow',
    };
  }
  if (pathname === '/roster') {
    return {
      title: `Dossiers | ${SITE_NAME}`,
      description: 'Operative dossiers from the Nightmare Syndicate roster.',
      robots: 'index,follow',
    };
  }
  if (pathname.startsWith('/roster/')) {
    const bandId = pathname.replace('/roster/', '').split('/')[0];
    const bandName = BAND_NAMES_BY_ID[bandId] ?? 'Band Dossier';
    return {
      title: `${bandName} | ${SITE_NAME}`,
      description:
        bandName !== 'Band Dossier'
          ? `${bandName} dossier, intelligence notes, and release links.`
          : 'Operative dossier for a Nightmare Syndicate roster unit.',
      robots: 'index,follow',
    };
  }
  if (pathname === '/releases') {
    return {
      title: `Releases | ${SITE_NAME}`,
      description: 'Catalog files, formats, and deployment status for current and archival releases.',
      robots: 'index,follow',
    };
  }
  if (pathname.startsWith('/releases/')) {
    return {
      title: `Release File | ${SITE_NAME}`,
      description: 'Release file, media, and purchase links for Nightmare Syndicate deployments.',
      robots: 'index,follow',
    };
  }
  if (pathname === '/merch') {
    return {
      title: `Merch | ${SITE_NAME}`,
      description: 'Official stock including CDs, shirts, and signed field posters.',
      robots: 'index,follow',
    };
  }
  if (pathname === '/dossiers') {
    return {
      title: `Charter | ${SITE_NAME}`,
      description: 'N.A.I.F.A. charter, doctrine, incident log, and internal memos.',
      robots: 'index,follow',
    };
  }
  if (pathname === '/contact') {
    return {
      title: `Contact | ${SITE_NAME}`,
      description: 'Direct channels for inquiries, submissions, and distro coordination.',
      robots: 'index,follow',
    };
  }
  if (pathname === '/login') {
    return {
      title: `Secure Access | ${SITE_NAME}`,
      description: 'Restricted portal for authorized Nightmare Syndicate personnel.',
      robots: 'noindex,nofollow',
    };
  }
  return {
    title: SITE_NAME,
    description: DEFAULT_DESCRIPTION,
    robots: 'index,follow',
  };
}

export function SeoManager() {
  const location = useLocation();

  useEffect(() => {
    const { title, description, robots } = getSeoConfig(location.pathname);
    const canonicalUrl = `${SITE_URL}${location.pathname === '/' ? '/' : location.pathname}`;

    document.title = title;
    upsertCanonical(canonicalUrl);

    upsertMetaByName('description', description);
    upsertMetaByName('robots', robots ?? 'index,follow');
    upsertMetaByName('twitter:title', title);
    upsertMetaByName('twitter:description', description);
    upsertMetaByName('twitter:image', SHARE_IMAGE_URL);

    upsertMetaByProperty('og:title', title);
    upsertMetaByProperty('og:description', description);
    upsertMetaByProperty('og:url', canonicalUrl);
    upsertMetaByProperty('og:image', SHARE_IMAGE_URL);
  }, [location.pathname]);

  return null;
}
